import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { companyShowApiClient } from '@src/api/company/auth/companyShowApiClient';
import {
  companyUpdateApiClient,
  CompanyUpdateErrorResponse,
} from '@src/api/company/auth/companyUpdateApiClient';
import { UserCompany } from '@src/api/types/userCompany';
import { closingMonth } from '@src/components/closingMonth';
import { employeeScales } from '@src/components/employeeScales';
import { industries } from '@src/components/industries';
import { prefectures } from '@src/components/prefectures';
import { revenueScales } from '@src/components/revenueScales';
import { useToast } from '@src/features/toast/context/toastContext';
import { useScrollToError } from '@src/hooks/useScrollToError';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button';
import ErrorMessage from '@src/ui/elements/form/ErrorMessage';
import Input from '@src/ui/elements/form/Input';
import Select from '@src/ui/elements/form/Select';
import Textarea from '@src/ui/elements/form/Textarea';
import Title from '@src/ui/elements/typography/Title.tsx';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import React, { useCallback, useEffect, useState } from 'react';

const CompaniesEdit: React.FC = () => {
  type FormValues = {
    name?: string;
    nameKana?: string;
    industry?: string;
    postalCodeFirst?: string;
    postalCodeSecond?: string;
    prefectureCode?: string;
    address?: string;
    familyName?: string;
    firstName?: string;
    familyNameKana?: string;
    firstNameKana?: string;
    birthday?: string;
    establishmentYear?: string;
    fiscalMonth?: string;
    serviceDescription?: string;
    revenusScale?: string;
    employeeScale?: string;
  };

  const [company, setCompany] = useState<UserCompany>();
  const [companyId, setCompanyId] = useState<number | null>();
  const [errors, setErrors] = useState<CompanyUpdateErrorResponse>();
  const toast = useToast();
  const [formValues, setFormValues] = useState<FormValues | undefined>();

  const fetchCompany = useCallback(async () => {
    const res = await companyShowApiClient.show();
    handleApiResponse(res, (data) => {
      setCompany(data.company);
      setCompanyId(data.company?.id);
      const initialFormValues: FormValues = {
        name: data.company?.name || '',
        nameKana: data.company?.name_kana || '',
        industry: data.company?.industry || '',
        postalCodeFirst: data.company?.postal_code?.slice(0, 3) || '',
        postalCodeSecond: data.company?.postal_code?.slice(3) || '',
        prefectureCode: data.company?.prefecture_code || '',
        address: data.company?.address || '',
        familyName: data.company?.family_name || '',
        firstName: data.company?.first_name || '',
        familyNameKana: data.company?.family_name_kana || '',
        firstNameKana: data.company?.first_name_kana || '',
        birthday: data.company?.birthday || '',
        establishmentYear: data.company?.establishment_year
          ? data.company?.establishment_year.toString()
          : '',
        fiscalMonth: data.company?.fiscal_month ? data.company.fiscal_month.toString() : '',
        serviceDescription: data.company?.service_description
          ? data.company?.service_description
          : '',
        revenusScale: data.company?.revenus_scale.toString() || '',
        employeeScale: data.company?.employee_scale.toString() || '',
      };
      setFormValues(initialFormValues);
    });
  }, []);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const update = async () => {
    setErrors(undefined);
    if (!formValues) {
      return;
    }

    const formatPostalCode = () => {
      if (!formValues.postalCodeFirst && !formValues.postalCodeSecond) {
        return '';
      }
      return `${formValues.postalCodeFirst}-${formValues.postalCodeSecond}`;
    };

    const response = await companyUpdateApiClient.update(Number(companyId), {
      company: {
        postal_code: formatPostalCode(),
        prefecture_code: formValues.prefectureCode,
        address: formValues.address,
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        birthday: formValues.birthday,
        establishment_year: formValues.establishmentYear,
        fiscal_month: formValues.fiscalMonth,
        industry: formValues.industry,
        service_description: formValues.serviceDescription,
        revenus_scale: formValues.revenusScale,
        employee_scale: formValues.employeeScale,
      },
    });
    handleApiResponse(
      response,
      () => {
        toast(TOAST_MESSAGE.UPDATE);
        fetchCompany();
        window.scrollTo(0, 0);
      },
      (errors) => {
        setErrors(errors);
      },
    );
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className='px-6 py-14'>
      <div>
        <Title>{company?.name}</Title>
        <div className='m-auto w-4/5'>
          <form
            className='mt-10 flex flex-col gap-[26px]'
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>企業名</label>
              </div>
              <div className='mb-2 text-[10px]'>
                ※変更が必要な場合は、
                <a
                  href={COMPANY_URL.CONTACT}
                  className='text-primary-500 underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  こちら
                </a>
                からお問い合わせください。
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex w-full flex-col'>
                  <div>{company?.name}</div>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>企業名（フリガナ）</label>
              </div>
              <div className='mb-2 text-[10px]'>
                ※変更が必要な場合は、
                <a
                  href={COMPANY_URL.CONTACT}
                  className='text-primary-500 underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  こちら
                </a>
                からお問い合わせください。
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex w-full flex-col'>
                  <div>{company?.name_kana}</div>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>本社所在地</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-[6px]'>
                  <div
                    className='flex items-center gap-[6px]'
                    ref={(el) => (fieldRefs.current['company.postal_code'] = el)}
                  >
                    <span>〒</span>
                    <div className='w-[100px]'>
                      <Input
                        size='m'
                        type='text'
                        placeholder='123'
                        value={formValues?.postalCodeFirst || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, postalCodeFirst: e.target.value })
                        }
                        error={!!errors?.company.postal_code}
                      />
                    </div>
                    <span>-</span>
                    <div className='w-[100px]'>
                      <Input
                        size='m'
                        type='text'
                        placeholder='4567'
                        value={formValues?.postalCodeSecond || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, postalCodeSecond: e.target.value })
                        }
                        error={!!errors?.company.postal_code}
                      />
                    </div>
                  </div>
                  <ErrorMessage>{firstError(errors?.company.postal_code)}</ErrorMessage>
                </div>
                <div className='flex flex-col gap-[6px]'>
                  <div className='flex gap-[6px]'>
                    <div
                      className='flex w-[164px] flex-[0_0_auto] flex-col gap-[6px]'
                      ref={(el) => (fieldRefs.current['company.prefecture_code'] = el)}
                    >
                      <Select
                        items={prefectures}
                        size='m'
                        initialValue={formValues?.prefectureCode || ''}
                        onSelect={(value) =>
                          setFormValues({ ...formValues, prefectureCode: value })
                        }
                        error={!!errors?.company.prefecture_code}
                      />
                      <ErrorMessage>{firstError(errors?.company.prefecture_code)}</ErrorMessage>
                    </div>
                    <div
                      className='flex w-full flex-col gap-[6px]'
                      ref={(el) => (fieldRefs.current['company.address'] = el)}
                    >
                      <Input
                        size='m'
                        type='text'
                        placeholder='〇〇市〇〇区1-2-3'
                        value={formValues?.address || ''}
                        onChange={(e) => setFormValues({ ...formValues, address: e.target.value })}
                        error={!!errors?.company.address}
                      />
                      <ErrorMessage>{firstError(errors?.company.address)}</ErrorMessage>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>代表者名</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex w-full flex-col gap-[6px]'>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.family_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='山田'
                      value={formValues?.familyName || ''}
                      onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                      error={!!errors?.company.family_name}
                    />
                    <ErrorMessage>{firstError(errors?.company.family_name)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.first_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='太郎'
                      value={formValues?.firstName || ''}
                      onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                      error={!!errors?.company.first_name}
                    />
                    <ErrorMessage>{firstError(errors?.company.first_name)}</ErrorMessage>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>代表者名（フリガナ）</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex w-full flex-col gap-[6px]'>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.family_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='ヤマダ'
                      value={formValues?.familyNameKana || ''}
                      onChange={(e) =>
                        setFormValues({ ...formValues, familyNameKana: e.target.value })
                      }
                      error={!!errors?.company.family_name_kana}
                    />
                    <ErrorMessage>{firstError(errors?.company.family_name_kana)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company.first_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='タロウ'
                      value={formValues?.firstNameKana || ''}
                      onChange={(e) =>
                        setFormValues({ ...formValues, firstNameKana: e.target.value })
                      }
                      error={!!errors?.company.first_name_kana}
                    />
                    <ErrorMessage>{firstError(errors?.company.first_name_kana)}</ErrorMessage>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>代表者生年月日</label>
              </div>
              <div className='flex h-[50px] gap-4'>
                <div
                  className='flex w-[200px] flex-col'
                  ref={(el) => (fieldRefs.current['company.birthday'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='19900101'
                    value={formValues?.birthday}
                    onChange={(e) => setFormValues({ ...formValues, birthday: e.target.value })}
                    error={hasError(errors?.company.birthday)}
                    inputMode='numeric'
                  />
                  <ErrorMessage>{firstError(errors?.company.birthday)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>設立年</label>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex w-full flex-col'>
                  <div className='flex items-center gap-[6px]'>
                    <div
                      className='w-[200px]'
                      ref={(el) => (fieldRefs.current['company.establishment_year'] = el)}
                    >
                      <Input
                        size='m'
                        type='text'
                        placeholder='2014'
                        value={formValues?.establishmentYear || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, establishmentYear: e.target.value })
                        }
                        error={!!errors?.company.establishment_year}
                      />
                    </div>
                    <span>年</span>
                  </div>
                </div>
                <ErrorMessage>{firstError(errors?.company.establishment_year)}</ErrorMessage>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>業種</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company.industry'] = el)}
                >
                  <Select
                    items={industries}
                    size='m'
                    initialValue={formValues?.industry || ''}
                    onSelect={(value) => setFormValues({ ...formValues, industry: value })}
                    error={!!errors?.company.industry}
                  />
                  <ErrorMessage>{firstError(errors?.company.industry)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>提供商品・サービス</label>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company.service_description'] = el)}
                >
                  <Textarea
                    error={!!errors?.company.service_description}
                    value={formValues?.serviceDescription || ''}
                    placeholder='例：AIを活用したデータ分析サービスや、企業向けクラウドサービスの提供'
                    onChange={(e) =>
                      setFormValues({ ...formValues, serviceDescription: e.target.value })
                    }
                  />
                  <ErrorMessage>{firstError(errors?.company.service_description)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>売上規模</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company.revenus_scale'] = el)}
                >
                  <Select
                    items={revenueScales}
                    size='m'
                    initialValue={formValues?.revenusScale || ''}
                    onSelect={(value) => setFormValues({ ...formValues, revenusScale: value })}
                    error={!!errors?.company.revenus_scale}
                  />
                  <ErrorMessage>{firstError(errors?.company.revenus_scale)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>従業員数規模</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company.employee_scale'] = el)}
                >
                  <Select
                    items={employeeScales}
                    size='m'
                    initialValue={formValues?.employeeScale || ''}
                    onSelect={(value) => setFormValues({ ...formValues, employeeScale: value })}
                    error={!!errors?.company.employee_scale}
                  />
                  <ErrorMessage>{firstError(errors?.company.employee_scale)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className='font-medium'>決算月</label>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company.fiscal_month'] = el)}
                >
                  <Select
                    items={closingMonth}
                    size='m'
                    initialValue={formValues?.fiscalMonth || ''}
                    onSelect={(value) => setFormValues({ ...formValues, fiscalMonth: value })}
                    error={!!errors?.company.fiscal_month}
                  />
                  <ErrorMessage>{firstError(errors?.company.fiscal_month)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='mt-auto flex justify-center gap-5 pt-4'>
              <div className='w-[172px]'>
                <Button
                  fullRounded
                  fullWidth
                  outline
                  onClick={update}
                >
                  保存する
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
};

export default CompaniesEdit;
